import Axios, { AxiosResponse, CancelTokenSource } from "axios";
import { BrowseOptions, BrowseOptionsParams, CreateCustomVariantForm, ModelCatalogFilterForm, ModelMatch, SourceVehiclePreview } from "../types/motorAtlasCatalogTypes";
import { createCacheLRU, serializeFilters } from "../utilities/simpleCache";
import http from "./httpService";
import getApiEndpoint from "./apiEndpoint";
import moment from "moment";

const apiEndpoint = `${getApiEndpoint()}/motorAtlas`;

const modelMatchesCache = createCacheLRU<AxiosResponse<{ data: ModelMatch[]; }>>(30, 180_000)
const cancelRef = { current: null as CancelTokenSource | null }
export async function findMotorAtlasEntries(
  values: ModelCatalogFilterForm,
  firstRegDate: string | null | undefined,
  commercialOnly: boolean,
  potentialEcodes: string[]
) {
  cancelRef.current?.cancel()
  cancelRef.current = Axios.CancelToken.source()

  const serialized = serializeFilters(values)
  const cached = modelMatchesCache.get(serialized)
  if (cached) {
    return cached
  }
  const searchParams = new URLSearchParams()

  if (potentialEcodes?.length && values?.potentialEcodesEnabled) {
    potentialEcodes.forEach(eCode => {
      searchParams.append("eCodes[]", String(eCode))
    })
  } else {
    if (values.type) {
      searchParams.set("type", String(values.type))
    }
    if (values.make) {
      searchParams.set("make", String(values.make))
    }
    if (values.model) {
      searchParams.set("model", String(values.model))
    }
    if (values.subModel) {
      searchParams.set("subModel", String(values.subModel))
    }

    if (values.year?.from && values.year?.to) {
      searchParams.set("yearFrom", String(values.year.from))
      searchParams.set("yearTo", String(values.year.to))
    }

    if (values.horsePower?.from && values.horsePower?.to) {
      searchParams.set("horsePowerFrom", String(values.horsePower.from))
      searchParams.set("horsePowerTo", String(values.horsePower.to))
    }

    if (values.batteryCapacity?.from && values.batteryCapacity?.to) {
      searchParams.set("batteryCapacityFrom", String(values.batteryCapacity.from))
      searchParams.set("batteryCapacityTo", String(values.batteryCapacity.to))
    }

    if (values.engineSize?.from && values.engineSize?.to) {
      searchParams.set("engineSizeFrom", String(values.engineSize.from))
      searchParams.set("engineSizeTo", String(values.engineSize.to))
    }

    if (values.fuelType && values.fuelType.length > 0) {
      values.fuelType.forEach(fuelType => {
        searchParams.append("fuelType[]", String(fuelType))
      })
    }
    if (values.gearbox && values.gearbox.length > 0) {
      values.gearbox.forEach(gearbox => {
        searchParams.append("gearbox[]", String(gearbox))
      })
    }
    if (values.bodyType && values.bodyType.length > 0) {
      values.bodyType.forEach(bodyType => {
        searchParams.append("bodyType[]", String(bodyType))
      })
    }
    if (values.driveType && values.driveType.length > 0) {
      values.driveType.forEach(driveType => {
        searchParams.append("driveType[]", String(driveType))
      })
    }
    if (firstRegDate) {
      const priceForYear = moment(firstRegDate).year()
      searchParams.set("priceForYear", String(priceForYear))
    }
    if (commercialOnly) {
      searchParams.set("commercialOnly", "1")
    }

  }


  const query = searchParams.toString()
  const response = await http.get<{ data: ModelMatch[] }>(`${apiEndpoint}/findEntries?${query}`, {
    cancelToken: cancelRef.current?.token ?? undefined
  });

  modelMatchesCache.set(serialized, response)

  return response
}


const modelBrowserCache = createCacheLRU<AxiosResponse<{ data: BrowseOptions[] }>>(30, 180_000)
export const deleteModelBrowserCacheKey = (values: BrowseOptionsParams) => {
  const cacheKey = JSON.stringify(values)
  modelBrowserCache.deleteKey(cacheKey)
}
/**
 * Expects the ecode parts of type, make, model, subModel
 * The backend will construct the full eCode from the eCode parts
 * $eCode = implode('', [$type, $make, $model, $subModel];
 * /misc/browseMotorAtlasOptions
 */
export async function browseMotorAtlasOptions(values: BrowseOptionsParams, extendedLookup?: boolean) {
  const cacheKey = JSON.stringify(values)
  const cachedResponse = modelBrowserCache.get(cacheKey)
  if (cachedResponse) {
    return cachedResponse
  }
  const searchParams = new URLSearchParams()

  if (values.type) {
    searchParams.set("type", values.type)
  }
  if (values.make) {
    searchParams.set("make", values.make)
  }
  if (values.model) {
    searchParams.set("model", values.model)
  }
  if (values.subModel) {
    searchParams.set("subModel", values.subModel)
  }
  if (extendedLookup) {
    searchParams.set("extendedLookup", "1")
  }


  const query = searchParams.toString()
  const response = await http.get<{ data: BrowseOptions[] }>(`${apiEndpoint}/browseOptions?${query}`);
  modelBrowserCache.set(cacheKey, response)
  return response
}


const transformedSourceCache = createCacheLRU<AxiosResponse<{ data: SourceVehiclePreview }>>(30, 900_000)
export async function motorAtlasTransformedSource(source: string, firstRegDate: string | null | undefined) {
  const cacheKey = source
  const cachedResponse = transformedSourceCache.get(cacheKey)
  if (cachedResponse) {
    return cachedResponse
  }

  const searchParams = new URLSearchParams()

  searchParams.set("source", source)
  if (firstRegDate) {
    const yearOfRegistration = moment(firstRegDate).year()
    searchParams.set("year", String(yearOfRegistration))
  }
  const query = searchParams.toString()
  const response = await http.get<{ data: SourceVehiclePreview }>(`${apiEndpoint}/transformedSource?${query}`);
  transformedSourceCache.set(cacheKey, response)
  return response
}



export async function storeCustomVariant(body: CreateCustomVariantForm) {
  const response = await http.post<CreateCustomVariantForm>(`${apiEndpoint}/storeCustomVariant`, body);
  return response
}
